import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'ls_site_access';
const ACCESS_CODE = 'localsite2026';

// Routes that bypass the SiteGate (have their own auth)
const BYPASS_PATHS = ['/super-admin', '/affiliation'];

export const SiteGate = ({ children }) => {
  const [granted, setGranted] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === 'granted') setGranted(true);
    setLoading(false);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === ACCESS_CODE) {
      sessionStorage.setItem(STORAGE_KEY, 'granted');
      setGranted(true);
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  if (loading) return null;

  // Bypass gate for specific paths
  const currentPath = window.location.pathname;
  if (BYPASS_PATHS.some(p => currentPath.startsWith(p))) return children;

  if (granted) return children;

  return <GateScreen code={code} setCode={setCode} error={error} onSubmit={handleSubmit} />;
};

const GateScreen = ({ code, setCode, error, onSubmit }) => {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(145deg, #1E1510 0%, #2D1F15 40%, #1A1410 100%)' }}
      data-testid="site-gate">

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ backgroundColor: '#E05D3A' }}>
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            LocalSite.Club
          </h1>
          <p className="text-white/40 text-sm mt-1">{t("Let's make friends", "Let's make friends")}</p>
        </div>

        {/* Gate Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#D4943A]" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <h2 className="font-bold text-white text-base">
              {t('Site en acces restreint', 'Restricted Access Site')}
            </h2>
          </div>
          <p className="text-white/50 text-xs mb-5">
            {t('Entrez le code d\'acces pour continuer', 'Enter the access code to continue')}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder={t('Code d\'acces...', 'Access code...')}
                autoFocus
                className={`w-full px-4 py-3 rounded-xl border text-white placeholder-white/30 text-sm font-medium tracking-wider text-center transition-all duration-200
                  ${error
                    ? 'bg-red-500/10 border-red-500/50 animate-[shake_0.3s_ease-in-out]'
                    : 'bg-white/5 border-white/10 focus:border-[#E05D3A]/50 focus:ring-2 focus:ring-[#E05D3A]/20'
                  }`}
                style={{ outline: 'none' }}
                data-testid="gate-code-input"
              />
              {error && (
                <p className="text-red-400 text-xs mt-2 text-center font-medium">
                  {t('Code invalide', 'Invalid code')}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: '#E05D3A' }}
              data-testid="gate-submit-btn"
            >
              {t('Entrer sur le site', 'Enter the site')}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-white/20 text-[10px] mt-6">
          {t('Ce site est actuellement en phase de developpement', 'This site is currently under development')}
        </p>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};

export default SiteGate;
